import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

type Props = {
  hlsUrl: string;
  isHover: boolean;
};

/**
 * The muted preview that plays over a video card's thumbnail while the pointer rests on it.
 *
 * The HLS pipeline is built on FIRST hover and kept afterwards — never on mount. A grid of
 * cards (Videos tab, or a videoCards content block) would otherwise open one hls.js
 * instance and fetch one manifest PER CARD on page load, and on touch devices — where
 * hover never fires — every byte of that is wasted.
 */
const HoverVideo: React.FC<Props> = ({ hlsUrl, isHover }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const readyRef = useRef(false);
  const pauseTimer = useRef<number | null>(null);

  // Latched by the first hover; the pipeline below is built once it flips.
  const [activated, setActivated] = useState(false);

  // Read by the load effect's async callbacks so they see the CURRENT hover state: the
  // pointer often leaves again before the manifest parses, and a stale `true` captured at
  // activation would start playback on a card nobody is pointing at.
  const isHoverRef = useRef(isHover);
  isHoverRef.current = isHover;

  useEffect(() => {
    if (isHover) setActivated(true);
  }, [isHover]);

  useEffect(() => {
    if (!activated) return undefined;

    const el = videoRef.current;
    if (!el) return undefined;

    readyRef.current = false;

    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {}
      hlsRef.current = null;
    }

    const markReadyAndMaybePlay = () => {
      readyRef.current = true;

      if (isHoverRef.current) {
        el.muted = true;
        el.playsInline = true;
        el.loop = true;
        el.play().catch(() => {});
      }
    };

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, markReadyAndMaybePlay);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) {
          try {
            hls.destroy();
          } catch {}
        }
      });

      hls.loadSource(hlsUrl);
      hls.attachMedia(el);
    } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = hlsUrl;
      el.addEventListener("canplay", markReadyAndMaybePlay, { once: true });

      return () => {
        el.removeEventListener("canplay", markReadyAndMaybePlay);
      };
    }

    return () => {
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;
    };
    // Deliberately NOT keyed on isHover: hovering in and back out used to tear the
    // pipeline down and re-fetch the manifest every single time. Play/pause is the
    // effect below.
  }, [hlsUrl, activated]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (pauseTimer.current) {
      window.clearTimeout(pauseTimer.current);
      pauseTimer.current = null;
    }

    if (isHover) {
      if (readyRef.current) {
        el.play().catch(() => {});
      }
    } else {
      pauseTimer.current = window.setTimeout(() => {
        try {
          el.pause();
        } catch {}
      }, 120);
    }
  }, [isHover]);

  // The element stays mounted before activation (it costs nothing without a source), so
  // the opacity cross-fade over the thumbnail behaves exactly as it always has.
  // `preload` stays "metadata": it is a no-op while no source is attached, and the
  // native-HLS path (Safari) relies on the browser loading enough to fire `canplay`.
  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="metadata"
      style={{
        opacity: isHover ? 1 : 0,
        transition: "opacity 120ms linear",
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        pointerEvents: "none",
        display: "block",
      }}
    />
  );
};

export default HoverVideo;
